import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "wheresthat_interview_draft";
const MAX_DRAFT_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

const INITIAL_STATE = {
  step: 1, // 1: Container Type, 2: Photo, 3: Analyzing, 4: Review Items, 5: Location, 6: Confirm
  containerType: "bin",
  photoUrl: null,
  photoBlob: null,
  containerName: "",
  items: [],
  locationId: null,
  analyzing: false,
  error: null,
  startedAt: null,
};

export function useInterviewState() {
  const [state, setState] = useState(() => {
    // Try to restore from localStorage
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const age = Date.now() - (parsed.startedAt || 0);
        if (age < MAX_DRAFT_AGE_MS) {
          return { ...INITIAL_STATE, ...parsed };
        }
      }
    } catch (e) {
      console.warn("Failed to restore interview draft:", e);
    }
    return { ...INITIAL_STATE, startedAt: Date.now() };
  });

  // Persist to localStorage on changes (except analyzing/error states)
  useEffect(() => {
    if (state.step > 1) {
      try {
        const toSave = {
          ...state,
          analyzing: false,
          error: null,
          photoBlob: null, // Don't persist blob
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      } catch (e) {
        console.warn("Failed to save interview draft:", e);
      }
    }
  }, [state]);

  const setContainerType = useCallback((type) => {
    setState((s) => ({ ...s, containerType: type }));
  }, []);

  const setPhoto = useCallback((url, blob = null) => {
    setState((s) => ({ ...s, photoUrl: url, photoBlob: blob }));
  }, []);

  const setContainerName = useCallback((name) => {
    setState((s) => ({ ...s, containerName: name }));
  }, []);

  const setItems = useCallback((items) => {
    setState((s) => ({ ...s, items }));
  }, []);

  const updateItem = useCallback((index, updates) => {
    setState((s) => ({
      ...s,
      items: s.items.map((item, i) => (i === index ? { ...item, ...updates } : item)),
    }));
  }, []);

  const removeItem = useCallback((index) => {
    setState((s) => ({
      ...s,
      items: s.items.filter((_, i) => i !== index),
    }));
  }, []);

  const addItem = useCallback(() => {
    setState((s) => ({
      ...s,
      items: [
        ...s.items,
        { name: "", quantity: 1, categories: [], is_consumable: false },
      ],
    }));
  }, []);

  const setLocationId = useCallback((id) => {
    setState((s) => ({ ...s, locationId: id }));
  }, []);

  const setStep = useCallback((step) => {
    setState((s) => ({ ...s, step, error: null }));
  }, []);

  const nextStep = useCallback(() => {
    setState((s) => ({ ...s, step: s.step + 1, error: null }));
  }, []);

  const prevStep = useCallback(() => {
    setState((s) => ({ ...s, step: Math.max(1, s.step - 1), error: null }));
  }, []);

  const setAnalyzing = useCallback((analyzing) => {
    setState((s) => ({ ...s, analyzing }));
  }, []);

  const setError = useCallback((error) => {
    setState((s) => ({ ...s, error, analyzing: false }));
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setState({ ...INITIAL_STATE, startedAt: Date.now() });
  }, []);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    state,
    setContainerType,
    setPhoto,
    setContainerName,
    setItems,
    updateItem,
    removeItem,
    addItem,
    setLocationId,
    setStep,
    nextStep,
    prevStep,
    setAnalyzing,
    setError,
    reset,
    clearDraft,
  };
}
